import React, { useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faCloudUploadAlt } from '@fortawesome/pro-regular-svg-icons';
import { getRecordVideoFeatureSelector, getUploadPodcastFeatureSelector, isMobileSelector } from 'common/selectors';

import { contentMaskHideAction } from '../../content-mask/actions';
import { DropDown, DropDownItem, DropDownMenu } from 'components/dropdown-nav';
import { getRecordScreenFeatureSelector, getUploadFeatureSelector } from '../../../../common/selectors/settings';
import { openWebcamRecordingDialog } from 'components/web-recording';
import { appRoutes } from 'routes';
import { uploadResetAction } from 'pages/upload.page/actions';

const CreateVideoMenu = ({ children }) => {
  const { t } = useTranslation();
  const menuCaretButton = useRef(null);
  const [isMenuShown, setIsMenuShown] = useState(false);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();

      menuCaretButton.current?.focus();
      setIsMenuShown(false);
    }
  };

  return (
    <DropDown
      isMenuShown={isMenuShown}
      className="ct-create-video-drop-down-nav"
      hasCaret={false}
      elementId={`create-video-menu`}
      onOpen={() => setIsMenuShown(true)}
      onClosing={() => setIsMenuShown(false)}
      onKeyDown={handleKeyDown}
    >
      <button
        className={`link link-button`}
        aria-label={`${t('create')} ${t('menu')}`}
        aria-haspopup="true"
        aria-expanded={isMenuShown}
        aria-controls="ct-global-create-menu"
        ref={menuCaretButton}
        onClick={() => setIsMenuShown(!isMenuShown)}
      >
        <FontAwesomeIcon aria-hidden="true" icon={faCloudUploadAlt} />
        {t('create')}
        <FontAwesomeIcon aria-hidden="true" icon={faAngleDown} />
      </button>

      {children && (
        <DropDownMenu role="menu" id="ct-global-create-menu">
          {children}
        </DropDownMenu>
      )}
    </DropDown>
  );
};

const UploadButton = ({ className, hideMenu, to, label }) => {
  return (
    <div data-e2e="upload-nav-item" className={`ct-upload-nav-item ${className ? className : ''}`}>
      <Link className="ct-upload-nav-item__link" to={to} onClick={hideMenu}>
        <div className="ct-upload-nav-item__content">
          <FontAwesomeIcon icon={faCloudUploadAlt}></FontAwesomeIcon>
          <span className="ct-upload-nav-item__label">{label}</span>
        </div>
      </Link>
    </div>
  );
};

const getIsDropdown = (upload, uploadPodcast, recordVideo, recordScreen, isMobile) => {
  // Dropdown not enabled on mobile
  if (isMobile) {
    return false;
  }

  // 'Record video' and 'Record screen' features depend on the 'Upload' feature.
  // If 'Upload' is enabled, then whenever we have another feature enabled, we'll see a dropdown.
  // If 'Upload' is disabled, even if 'Podcast' is enabled, we will see only a button,
  // because the other options will be disabled as well.
  return upload && (recordVideo || recordScreen || uploadPodcast);
};

export const CreateVideoNavItem = connect(
  (state) => ({
    uploadEnabled: getUploadFeatureSelector(state),
    uploadPodcastEnabled: getUploadPodcastFeatureSelector(state),
    recordVideoEnabled: getRecordVideoFeatureSelector(state),
    recordScreenEnabled: getRecordScreenFeatureSelector(state),
    isMobile: isMobileSelector(state),
  }),
  (dispatch) => ({
    hideMenu: () => {
      dispatch(uploadResetAction());
      dispatch(contentMaskHideAction());
    },
  }),
  (stateProps, dispatchProps, props) => ({
    ...stateProps,
    ...dispatchProps,
    ...props,
  })
)((props) => {
  const {
    className,
    hideMenu,
    uploadEnabled,
    recordVideoEnabled,
    recordScreenEnabled,
    uploadPodcastEnabled,
    isMobile,
  } = props;
  const isDropdown = getIsDropdown(
    uploadEnabled,
    uploadPodcastEnabled,
    recordVideoEnabled,
    recordScreenEnabled,
    isMobile
  );
  const { t } = useTranslation();
  const route = uploadEnabled ? '/upload' : '/upload-podcast';
  const label = uploadEnabled ? t('upload') : t('upload_podcast');
  const dispatch = useDispatch();

  const onUploadClick = (e) => {
    hideMenu();
    e.stopPropagation();
  };

  const isVideoRecordingSupported =
    navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia || navigator.msGetUserMedia;

  return isDropdown ? (
    <CreateVideoMenu>
      {uploadEnabled && (
        <DropDownItem key={'menu-item-upload'} elementId="upload">
          <Link role="menuitem" to={appRoutes.upload} onClick={onUploadClick}>
            {t('upload_video')}
          </Link>
        </DropDownItem>
      )}
      {uploadPodcastEnabled && (
        <DropDownItem key={'menu-item-upload-podcast'} elementId="upload-podcast">
          <Link role="menuitem" to={appRoutes.uploadPodcast} onClick={onUploadClick}>
            {t('upload_podcast')}
          </Link>
        </DropDownItem>
      )}
      {isVideoRecordingSupported && recordVideoEnabled && (
        <DropDownItem key={'menu-item-record'} elementId="record-video">
          <button
            role="menuitem"
            className="link-button"
            onClick={() => openWebcamRecordingDialog(dispatch, { type: 'webcam' })}
          >
            {t('record_video')}
          </button>
        </DropDownItem>
      )}
      {isVideoRecordingSupported && recordScreenEnabled && (
        <DropDownItem key={'menu-item-screen'} elementId="record-screen">
          <button
            role="menuitem"
            className="link-button"
            onClick={() => openWebcamRecordingDialog(dispatch, { type: 'screen' })}
          >
            {t('record_screen')}
          </button>
        </DropDownItem>
      )}
    </CreateVideoMenu>
  ) : (
    (uploadEnabled || uploadPodcastEnabled) && (
      <UploadButton className={className} hideMenu={hideMenu} to={route} label={label}></UploadButton>
    )
  );
});
